import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { MagnifyingGlass, Users as UsersIcon, Key, ShieldCheck, Copy, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function generateTempPassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const arr = new Uint32Array(10);
  window.crypto.getRandomValues(arr);
  let p = "";
  for (let i = 0; i < 10; i++) p += chars[arr[i] % chars.length];
  return p + "9!";
}

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      const { data } = await axios.get(`${API}/admin/all-users?${params}`, { withCredentials: true });
      setUsers(data.users || []);
    } catch { toast.error("Failed to load users"); }
    setLoading(false);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(fetchUsers, 300);
    return () => clearTimeout(t);
  }, [fetchUsers]);

  const openReset = (u) => { setTarget(u); setNewPassword(""); };

  const handleGenerate = () => setNewPassword(generateTempPassword());

  const handleCopy = () => {
    navigator.clipboard.writeText(newPassword);
    toast.success("Password copied");
  };

  const handleSetPassword = async () => {
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/users/${target.id}/set-password`, { new_password: newPassword }, { withCredentials: true });
      toast.success(`Password updated for ${target.email}`);
      setTarget(null);
      setNewPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to set password");
    }
    setSaving(false);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-users-page">
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">ADMIN</p>
        <h1 className="font-[Chivo] text-3xl sm:text-4xl font-black tracking-tighter text-zinc-950 mt-1 flex items-center gap-3">
          <UsersIcon size={32} weight="fill" /> Users
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Manage every account and reset passwords.</p>
      </div>

      <div className="relative mb-4">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          placeholder="Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-10 rounded-none"
          data-testid="users-search-input"
        />
      </div>

      <div className="border border-zinc-200 overflow-x-auto" data-testid="users-table">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Name</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Email</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Role / Clinics</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="py-12 text-center text-sm text-zinc-500 font-mono">LOADING...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={4} className="py-12 text-center text-sm text-zinc-500">No users found</td></tr>
            ) : (
              users.map(u => (
                <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 px-4 text-sm font-medium text-zinc-800">{u.name || "—"}</td>
                  <td className="py-3 px-4 text-sm text-zinc-600">{u.email}</td>
                  <td className="py-3 px-4 text-sm">
                    {u.is_master_admin ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono uppercase tracking-wider bg-zinc-950 text-white" data-testid={`badge-master-${u.id}`}>
                        <ShieldCheck size={12} weight="fill" /> Master Admin
                      </span>
                    ) : u.clinics?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {u.clinics.map((c, i) => (
                          <span key={i} className="inline-block px-1.5 py-0.5 text-[10px] font-mono bg-zinc-100 text-zinc-600 border border-zinc-200">
                            {c.org_name}{c.role === "org_admin" ? " (admin)" : ""}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-zinc-400">No active clinic</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      onClick={() => openReset(u)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-zinc-100 text-zinc-700 border border-zinc-200 hover:bg-zinc-200 transition-colors"
                      data-testid={`set-password-btn-${u.id}`}
                    >
                      <Key size={14} /> Set Password
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Set Password Dialog */}
      <Dialog open={!!target} onOpenChange={(o) => { if (!o) { setTarget(null); setNewPassword(""); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-[Chivo]">Set Password</DialogTitle></DialogHeader>
          {target && (
            <div className="space-y-4">
              <div className="p-3 bg-zinc-50 border border-zinc-200 text-sm">
                <p className="font-medium text-zinc-950">{target.name || target.email}</p>
                <p className="text-xs text-zinc-500">{target.email}</p>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">New Password</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    className="rounded-none flex-1"
                    placeholder="Type a password or generate one"
                    data-testid="new-password-field"
                  />
                  <button onClick={handleGenerate} title="Generate" className="px-3 bg-[#0055FF] text-white hover:bg-[#0044CC] transition-colors" data-testid="generate-password-btn">
                    <ArrowsClockwise size={16} />
                  </button>
                  {newPassword && (
                    <button onClick={handleCopy} title="Copy" className="px-3 bg-zinc-100 text-zinc-700 border border-zinc-200 hover:bg-zinc-200 transition-colors" data-testid="copy-password-btn">
                      <Copy size={16} />
                    </button>
                  )}
                </div>
                <p className="text-xs text-zinc-400 mt-1.5">Share this password with the user. They can change it later via "Forgot password".</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => { setTarget(null); setNewPassword(""); }} className="px-4 py-2 text-sm font-semibold bg-zinc-100 text-zinc-700 border border-zinc-200 hover:bg-zinc-200 transition-colors" data-testid="cancel-set-password">Cancel</button>
            <button onClick={handleSetPassword} disabled={saving || newPassword.length < 6} className="px-4 py-2 text-sm font-semibold bg-[#0055FF] text-white hover:bg-[#0044CC] transition-colors disabled:opacity-50" data-testid="confirm-set-password">
              {saving ? "Saving..." : "Set Password"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
